import { unwrap, type MemberOut, type RoleOut, type TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Combobox, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useErrorMessage } from "../lib/api-errors";
import { FormAlert } from "../auth/components/form-alert";
import { useApi } from "../session/session-provider";
import { EffectivePermissions } from "./effective-permissions";
import { ALL_SCOPE, ScopePicker, isScopeComplete, type ScopeValue } from "./scope-picker";
import { useEffectivePreview, useInvalidateAccess } from "./use-roles";

interface AssignMemberDialogProps {
  role: RoleOut;
  members: readonly MemberOut[];
  teams: readonly TeamOut[];
  totalPermissions: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const toInput = (m: MemberOut) => m.assignments.map((a) => ({ role_id: a.role_id, scope_type: a.scope_type, scope_ids: a.scope_ids }));

/** Adds `role` (with a scope) to a member's assignments; previews the union before saving. */
export function AssignMemberDialog({ role, members, teams, totalPermissions, open, onOpenChange }: AssignMemberDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const memberFieldId = useId();
  const [memberId, setMemberId] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeValue>(ALL_SCOPE);
  const [error, setError] = useState<string | null>(null);
  const candidates = members.filter((m) => !m.is_you && m.status === "active" && !m.assignments.some((a) => a.role_id === role.id));
  const member = candidates.find((m) => m.id === memberId);
  const assignments = member ? [...toInput(member), { role_id: role.id, ...scope }] : [];
  const preview = useEffectivePreview({ assignments }, open && Boolean(member) && isScopeComplete(scope));

  const save = useMutation({
    mutationFn: async () => {
      if (!member) return;
      await unwrap(api.PUT("/v1/members/{membership_id}/roles", { params: { path: { membership_id: member.id } }, body: { assignments } }));
    },
    onSuccess: async () => {
      toast.success(t("roles.assign.assigned", { role: role.name, name: member?.name ?? "" }));
      await invalidate();
      setMemberId(null);
      setScope(ALL_SCOPE);
      onOpenChange(false);
    },
    onError: (caught) => { setError(message(caught)); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t("roles.assign.dialogTitle", { role: role.name })}
        description={t("roles.assign.dialogSubtitle")}
        closeLabel={t("common.close")}
        className="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("common.cancel")}
            </Button>
            <Button loading={save.isPending} disabled={!member || !isScopeComplete(scope) || save.isPending} onClick={() => { save.mutate(); }}>
              <Icon name="user-plus" />
              {t("roles.assign.submit")}
            </Button>
          </>
        }
      >
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
          <div>
            <label className="label" htmlFor={memberFieldId}>
              {t("roles.assign.member")}
            </label>
            <Combobox
              id={memberFieldId}
              options={candidates.map((m) => ({ value: m.id, label: m.name, description: m.email }))}
              value={memberId}
              onValueChange={(id) => {
                setError(null);
                setMemberId(id);
              }}
              labels={{ placeholder: t("roles.assign.chooseMember"), search: t("roles.assign.searchMembers"), empty: t("roles.assign.noMembers") }}
            />
          </div>
          <ScopePicker value={scope} onChange={setScope} teams={teams} />
          {member ? (
            <div className="rounded-lg border border-line p-4">
              <EffectivePermissions
                preview={preview.data}
                isPending={preview.isFetching}
                isError={preview.isError}
                total={totalPermissions}
                title={t("roles.effective.memberTitle", { name: member.name })}
                subtitle={t("roles.effective.memberSubtitle")}
              />
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
